'use client'

import { useTranslations } from 'next-intl'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import * as React from 'react'
import { flushSync } from 'react-dom'

import { Tab, TabContent, TabList, TabTrigger } from 'component/ui/tabs'
import { TabKey } from 'type/search'

const SomeEnum =
  <T extends Object>(e: T) =>
  (token: any): token is T[keyof T] =>
    Object.values(e).includes(token as T[keyof T])

const isTabKey = SomeEnum(TabKey)

const SearchTab = ({
  media,
  community,
  comment,
  user,
  hashtag
}: {
  media: React.ReactNode
  community: React.ReactNode
  comment: React.ReactNode
  user: React.ReactNode
  hashtag: React.ReactNode
}) => {
  const t = useTranslations()
  const [tab, setTab] = useQueryState(
    'tab',
    parseAsStringEnum<TabKey>(Object.values(TabKey)).withDefault(TabKey.Spot)
  )

  const scrollTop = React.useRef({
    [TabKey.Spot]: 0,
    [TabKey.Community]: 0,
    [TabKey.User]: 0,
    [TabKey.Hashtag]: 0,
    [TabKey.Comment]: 0
  })

  const handleChange = (value: string) => {
    if (!isTabKey(value)) {
      return
    }

    // record current scroll position
    scrollTop.current[tab] = window.scrollY

    flushSync(() => {
      setTab(value)
    })

    // scroll to previous position
    window.scrollTo({
      top: scrollTop.current[value]
    })
  }

  return (
    <Tab orientation='horizontal' value={tab} onValueChange={handleChange}>
      <TabList className='[--tab-list-top:128px]'>
        <TabTrigger value={TabKey.Spot} className='w-1/5'>
          {t('common.media')}
        </TabTrigger>
        <TabTrigger value={TabKey.Community} className='w-1/5'>
          {t('common.communities')}
        </TabTrigger>
        <TabTrigger value={TabKey.Comment} className='w-1/5'>
          {t('common.posts')}
        </TabTrigger>
        <TabTrigger value={TabKey.User} className='w-1/5'>
          {t('common.users')}
        </TabTrigger>
        <TabTrigger value={TabKey.Hashtag} className='w-1/5'>
          {t('common.hashtags')}
        </TabTrigger>
      </TabList>
      <TabContent value={TabKey.Spot}>{media}</TabContent>
      <TabContent value={TabKey.Community}>{community}</TabContent>
      <TabContent value={TabKey.Comment}>{comment}</TabContent>
      <TabContent value={TabKey.User}>{user}</TabContent>
      <TabContent value={TabKey.Hashtag}>{hashtag}</TabContent>
    </Tab>
  )
}

export default SearchTab
